import { useEffect, useRef } from 'react';
import { AppState, type AppStateStatus } from 'react-native';

/**
 * Fires `onResume` when the app transitions to the active (foreground)
 * state. Cross-platform: on web, react-native-web bridges `AppState` to
 * `document.visibilityState`, so the same hook works on iOS, Android,
 * and web without a Platform-specific branch.
 *
 * Uses a ref so the consumer doesn't have to memoize the callback —
 * `useAppResume(() => doStuff())` just works. Adding a dep array would
 * force every callsite into useCallback boilerplate that the hook itself
 * doesn't need.
 *
 * Examples in the codebase:
 *   - AuthContext — refresh access token if it's near expiry on resume
 *   - useTripSync — hydrate trip data from server on resume
 */
export function useAppResume(onResume: () => void): void {
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'active') onResumeRef.current();
    });
    return () => sub.remove();
  }, []);
}

/**
 * Fires `onBackground` when the app leaves the active state — either
 * suspended ('inactive', a brief intermediate phase on iOS) or fully
 * backgrounded ('background').
 *
 * Use for lifecycle bookends: flush pending writes before the OS may
 * suspend the JS engine, persist transient UI state, etc.
 *
 * Example: SyncManagerInit calls `flushImmediate()` here so closing the
 * tab / backgrounding the app doesn't drop in-flight mutations.
 */
export function useAppBackground(onBackground: () => void): void {
  const onBackgroundRef = useRef(onBackground);
  onBackgroundRef.current = onBackground;

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'background' || state === 'inactive') {
        onBackgroundRef.current();
      }
    });
    return () => sub.remove();
  }, []);
}
